/**
 * useAuth — single source of truth for the current user.
 *
 * - Calls GET /api/auth/me on mount with credentials:'include' (so the
 *   httpOnly session_token cookie is sent automatically).
 * - Skips the /me call if the URL hash contains `session_id=` — that
 *   means we just returned from the Emergent OAuth round-trip and the
 *   AuthCallback component is about to exchange the id and set the
 *   cookie. Calling /me too early would 401 and force a spurious
 *   logout state.
 * - signIn() redirects to Emergent's OAuth page; on return, the user
 *   lands at `/dashboard#session_id=<id>` and AuthCallback finishes.
 * - signOut() POSTs /api/auth/logout and clears local state.
 */

import { useCallback, useEffect, useState } from "react";
import { getUserToken } from "@/lib/userToken";

const API = process.env.REACT_APP_BACKEND_URL;

let _cached = { user: null, plan: null, loaded: false };
const _subscribers = new Set();
// iter-176 — In-flight promise dedupes concurrent _fetchMe() calls.
// Without this, when 9 components using useAuth() mount in the same
// React render pass, each one sees `_cached.loaded === false` (the
// first fetch hasn't resolved yet) and independently fires its own
// /api/auth/me request — observed 36 calls during a single anonymous
// bootstrap. With the dedupe, the first caller starts the fetch and
// every subsequent caller awaits the same promise.
let _inflightFetch = null;

function _notify() {
  _subscribers.forEach((cb) => {
    try { cb(_cached); } catch (e) { void e; }
  });
}

async function _fetchMe() {
  if (_inflightFetch) return _inflightFetch;
  _inflightFetch = (async () => {
    try {
      const res = await fetch(`${API}/api/auth/me`, { credentials: "include" });
      if (!res.ok) {
        _cached = { user: null, plan: null, loaded: true };
      } else {
        const user = await res.json();
        // Fetch plan in parallel (best-effort — failure leaves plan: null and
        // the UI degrades to "Upgrade" CTA).
        let plan = null;
        try {
          const pres = await fetch(`${API}/api/billing/plan`, { credentials: "include" });
          if (pres.ok) plan = await pres.json();
        } catch (e) { void e; }
        _cached = { user, plan, loaded: true };
        // iter-156 — REMOVED the localStorage["elpm-user-token"] = user.user_id
        // overwrite that lived here pre-iter-156. That write leaked the
        // authenticated user_id to localStorage, where XSS (if ever introduced)
        // could read it and forge X-User-Token headers against the Library API.
        // The anonymous per-browser UUID stays in localStorage (not sensitive),
        // and authenticated requests now rely on the httpOnly session_token
        // cookie sent via credentials: "include". Library routes already trust
        // the cookie path; non-library writes that still use X-User-Token are
        // limited to the per-browser anonymous scope by design.
      }
    } catch (e) {
      void e;
      _cached = { user: null, plan: null, loaded: true };
    } finally {
      _inflightFetch = null;
    }
    _notify();
  })();
  return _inflightFetch;
}

/**
 * Public hook. Returns the current user (or null), a loaded flag, and the
 * signIn/signOut/refresh helpers. Components can call signIn() with an
 * optional `next` param to override where to return after auth — defaults
 * to the current page.
 */
export default function useAuth() {
  const [snap, setSnap] = useState(_cached);

  useEffect(() => {
    _subscribers.add(setSnap);
    if (!_cached.loaded) {
      // CRITICAL: If returning from OAuth, skip the /me probe — AuthCallback
      // will set the cookie and re-fetch /me when it's done. Otherwise the
      // first /me here 401s and triggers a UI flash of "Sign In" before the
      // callback lands.
      if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
        _cached = { user: null, plan: null, loaded: false };
      } else {
        _fetchMe();
      }
    }
    return () => { _subscribers.delete(setSnap); };
  }, []);

  const signIn = useCallback((next) => {
    if (typeof window === "undefined") return;
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/dashboard"
      + (next ? `?next=${encodeURIComponent(next)}` : "");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    } catch (e) { void e; }
    _cached = { user: null, plan: null, loaded: true };
    _notify();
    // Reset the localStorage user_token back to a fresh anonymous UUID so the
    // signed-out browser doesn't keep writing to the previous user's scope.
    try {
      window.localStorage.removeItem("elpm-user-token");
      getUserToken();  // regenerate
    } catch (e) { void e; }
  }, []);

  const refresh = useCallback(() => {
    _cached = { user: null, plan: null, loaded: false };
    return _fetchMe();
  }, []);

  /**
   * Patch the signed-in user's preferences (currently just theme background).
   * Persists to backend + refreshes the local cache so AppShell can re-apply
   * the new background immediately.
   */
  const updatePreferences = useCallback(async (patch) => {
    if (!patch || Object.keys(patch).length === 0) return null;
    const res = await fetch(`${API}/api/auth/preferences`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    const data = await res.json();
    // Locally merge into cache and notify subscribers so the next render
    // picks up the new prefs without a full /me re-fetch round-trip.
    if (_cached.user) {
      _cached = {
        ..._cached,
        user: { ..._cached.user, preferences: data.preferences || {} },
      };
      _notify();
    }
    return data.preferences;
  }, []);

  return { user: snap.user, plan: snap.plan, loading: !snap.loaded, signIn, signOut, refresh, updatePreferences };
}

/** Test-only — clear the in-module cache so the next mount re-fetches. */
export function __resetAuthCache() {
  _cached = { user: null, plan: null, loaded: false };
  _notify();
}
